// frontend/src/components/RegistrationRequestsPage.js

import React, { useState, useEffect } from 'react';
import api from '../services/api';

const RegistrationRequestsPage = ({ token, onVoltar }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchRegistrationRequests();
  }, []);

  // Efeito para atualizar o estado de "selecionar todos" quando as solicitações mudarem
  useEffect(() => {
    // Como agora 'requests' já contém apenas solicitações pendentes, não precisamos filtrar novamente
    const pendingRequestIds = requests.map(r => r._id);
    // Considerar "selecionar todos" como verdadeiro se todas as solicitações pendentes (até 8) estiverem selecionadas
    const pendingRequestsToCheck = pendingRequestIds.slice(0, 8);
    const allPendingSelected = pendingRequestsToCheck.length > 0 &&
                               pendingRequestsToCheck.every(id => selectedRequests.includes(id));
    setSelectAll(allPendingSelected);
  }, [requests, selectedRequests]);

  const fetchRegistrationRequests = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await api.get('/api/registration-requests', config);
      // Filtrar apenas as solicitações pendentes (não aceitas)
      const pendingRequests = response.data.requests.filter(request => request.status === 'pendente');
      setRequests(pendingRequests);
      setError('');
    } catch (err) {
      console.error('Erro ao buscar solicitações de registro:', err);
      setError('Erro ao carregar solicitações de registro. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const toggleRequestSelection = (requestId) => {
    if (selectedRequests.includes(requestId)) {
      // Remover da seleção
      const newSelected = selectedRequests.filter(id => id !== requestId);
      setSelectedRequests(newSelected);
      setSelectAll(false);
    } else if (selectedRequests.length < 8) {
      // Adicionar à seleção
      setSelectedRequests([...selectedRequests, requestId]);
    } else {
      // Mostrar aviso quando o limite for atingido
      alert('Limite de 8 seleções atingido. Desmarque alguma seleção para adicionar novas.');
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      // Desselecionar todas
      setSelectedRequests([]);
      setSelectAll(false);
    } else {
      // Selecionar todas as solicitações pendentes (até 8)
      const allRequestIds = requests.map(r => r._id);
      if (allRequestIds.length === 0) {
        return; // Não faz nada se não houver solicitações
      }
      setSelectedRequests(allRequestIds.slice(0, 8));
      setSelectAll(true);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRequests.length === 0) return;

    if (!window.confirm(`Tem certeza que deseja aprovar ${selectedRequests.length} solicitações de registro?`)) {
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      // Aprovar cada solicitação selecionada individualmente
      let successCount = 0;
      const errors = [];

      for (const requestId of selectedRequests) {
        try {
          await api.put(`/api/registration-requests/${requestId}/approve`, {}, config);
          successCount++;
        } catch (error) {
          errors.push({ id: requestId, error: error.message });
        }
      }

      // Atualizar a lista
      fetchRegistrationRequests(); // Isso já filtra apenas as pendentes
      setSelectedRequests([]);
      setSelectAll(false);

      if (errors.length > 0) {
        alert(`Aprovação concluída parcialmente: ${successCount} aprovadas com sucesso, ${errors.length} com erro.`);
      } else {
        alert(`Aprovação concluída: ${successCount} solicitações aprovadas com sucesso.`);
      }
    } catch (err) {
      console.error('Erro ao aprovar solicitações de registro:', err);
      alert('Erro ao aprovar solicitações de registro. Tente novamente.');
    }
  };

  const handleBulkReject = async () => {
    if (selectedRequests.length === 0) return;

    const reason = prompt('Digite o motivo da rejeição para todas as solicitações selecionadas:');
    if (!reason) return;

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      // Rejeitar cada solicitação selecionada individualmente
      let successCount = 0;
      const errors = [];

      for (const requestId of selectedRequests) {
        try {
          await api.put(`/api/registration-requests/${requestId}/reject`, { motivo: reason }, config);
          successCount++;
        } catch (error) {
          errors.push({ id: requestId, error: error.message });
        }
      }

      // Atualizar a lista
      fetchRegistrationRequests(); // Isso já filtra apenas as pendentes
      setSelectedRequests([]);
      setSelectAll(false);

      if (errors.length > 0) {
        alert(`Rejeição concluída parcialmente: ${successCount} rejeitadas com sucesso, ${errors.length} com erro.`);
      } else {
        alert(`Rejeição concluída: ${successCount} solicitações rejeitadas com sucesso.`);
      }
    } catch (err) {
      console.error('Erro ao rejeitar solicitações de registro:', err);
      alert('Erro ao rejeitar solicitações de registro. Tente novamente.');
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Tem certeza que deseja aprovar esta solicitação de registro?')) {
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      await api.put(`/api/registration-requests/${requestId}/approve`, {}, config);

      // Refresh the list
      fetchRegistrationRequests(); // Isso já filtra apenas as pendentes
      // Remove from selected requests if it was selected
      if (selectedRequests.includes(requestId)) {
        setSelectedRequests(selectedRequests.filter(id => id !== requestId));
      }
    } catch (err) {
      console.error('Erro ao aprovar solicitação de registro:', err);
      alert('Erro ao aprovar solicitação de registro. Tente novamente.');
    }
  };

  const handleReject = async (requestId, reason) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      await api.put(`/api/registration-requests/${requestId}/reject`, { motivo: reason }, config);

      // Close modal and refresh the list
      setShowRejectModal(false);
      setRejectRequestId(null);
      setRejectReason('');
      fetchRegistrationRequests(); // Isso já filtra apenas as pendentes
      // Remove from selected requests if it was selected
      if (selectedRequests.includes(requestId)) {
        setSelectedRequests(selectedRequests.filter(id => id !== requestId));
      }
    } catch (err) {
      console.error('Erro ao rejeitar solicitação de registro:', err);
      alert('Erro ao rejeitar solicitação de registro. Tente novamente.');
    }
  };

  const openRejectModal = (requestId) => {
    setRejectRequestId(requestId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const getStatusColor = (status) => {
    // Agora todas as solicitações exibidas são pendentes
    return 'bg-yellow-100 text-yellow-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando solicitações de registro...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6FC]">
      <div className="container mx-auto p-4 sm:p-6 md:p-8">
        <header className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Solicitações de Registro Pendentes
          </h1>
          <button
            onClick={() => onVoltar('admin')}
            className="bg-violet-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 shadow-sm transition-all duration-300"
          >
            Voltar
          </button>
        </header>

        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md border border-[#E0CCFF] overflow-hidden">
          {/* Controles de seleção múltipla para solicitações pendentes */}
          {requests.length > 0 && (
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectAll && requests.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      disabled={requests.length === 0}
                    />
                    <span className="ml-2 text-sm text-gray-700">Selecionar todos</span>
                  </label>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleBulkApprove}
                      disabled={selectedRequests.length === 0}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        selectedRequests.length === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      Aprovar ({selectedRequests.length})
                    </button>

                    <button
                      onClick={handleBulkReject}
                      disabled={selectedRequests.length === 0}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        selectedRequests.length === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      Rejeitar ({selectedRequests.length})
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-500">
                  {selectedRequests.length} de 8 selecionados
                  {selectedRequests.length >= 8 && (
                    <span className="ml-2 text-xs text-yellow-600">(limite atingido)</span>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500 italic">
                Limite de 8 usuários por operação
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={selectAll && requests.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      disabled={requests.length === 0}
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-mail
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      Não há solicitações de registro pendentes
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request._id} className={`${selectedRequests.includes(request._id) ? 'bg-violet-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedRequests.includes(request._id)}
                          onChange={() => toggleRequestSelection(request._id)}
                          className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                          disabled={selectedRequests.length >= 8 && !selectedRequests.includes(request._id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{request.nome}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{request.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(request.status)}`}>
                          Pendente
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(request.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleApprove(request._id)}
                            className="text-green-600 hover:text-green-900 font-medium"
                          >
                            Aprovar
                          </button>
                          <button
                            onClick={() => openRejectModal(request._id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Rejeitar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Rejeição */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Motivo da Rejeição</h3>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectRequestId(null);
                  setRejectReason('');
                }}
                className="text-gray-500 hover:text-gray-800 text-2xl"
              >
                &times;
              </button>
            </div>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Digite o motivo da rejeição..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-violet-500 focus:border-violet-500 min-h-32"
              required
            />

            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectRequestId(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleReject(rejectRequestId, rejectReason)}
                className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                disabled={!rejectReason.trim()}
              >
                Rejeitar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationRequestsPage;